import Vue from 'vue';
import Vuex from 'vuex';

import createPersistedState from 'vuex-persistedstate';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getUnixTime, startOfDay, endOfDay, add, eachWeekOfInterval, eachMonthOfInterval, isEqual, fromUnixTime, isAfter } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import horsesFilters, { defaultActiveState as horsesFiltersDefaults } from '@/store/modules/horsesFilters';
import sessionsFilters, { defaultActiveState as sessionsFiltersDefaults } from '@/store/modules/sessionsFilters';

const { equestrianReference, getSiteInfo, openSearchProducts, getHorses, getPlaces, getDisciplines, getInstructors } = require('../helpers/request');
const { isObjectEmpty, sortArray, isMobileView, isSameMonth, minutesToHours } = require('../helpers/utils');
const { sessionContentType, getContentTypeSessionsByPeriod, filterSessionByInterval, getDatesBetweenDates } = require('../helpers/sessions');
const { isContentTypeAvailable, separateContentTypesWithAndWithoutSession, computeDailyWeeklyHorseEffort, computeEffort } = require('../helpers/dataManager');
const { displayOnPlanner } = require('../helpers/variables');
const { contentTypeSessions, getSessionsByPeriodWithContentType } = require('../helpers/amplify');

Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [createPersistedState({
    storage: window.sessionStorage,
    paths: [
      'workspace', 'selectedDate', 'user', 'siteInfo', 'selectedCustomers', 'firstLoad'
    ]
  })],
  state: {
    algoliaCustomers: [],
    selectedCustomers: [],
    user: {},
    workspace: {
      code: 'alpha_bv',
      name: '(SC) BAYARD-VINCENNES'
    },
    selectedDate: null,
    loading: false,
    search: null,
    currentTab: 'customer',
    references: null,
    filters: null,
    filterType: null,
    horses: [],
    places: [],
    instructors: [],
    sessions: [],
    disciplines: [],
    socket: null,
    sideBarFilters: null,
    filterResults: [],
    customers: [],
    contentTypeAllSessions: [],
    componentLoader: false,
    amplifySubscription: null,
    siteInfo: null,
    products: [],
    workspaces: [],
    instructorSessions: [],
    availableHorses: [],
    selectedSession: null,
    isSessionFilterApplied: false,
    showSessionsOfType: [],
    customersToAssign: [],
    currentStep: null,
    showOriginalSessionColor: false,
    showDisciplineColor: false,
    monthlySessions: [],
    webSocketError: null,
    debugMode: false,
    networkLogs: [],
    showMaxEffortModal: false,
    maxEffortCallback: null,
    alertEffort: null,
    workspaceActivitiesFilter: [],
    isLazyLoaded: {
      horses: false,
      places: false,
      instructors: false,
      products: false
    },
    synchronizePlanning: true,
    swapHorseModal: { show: false, callback: null },
    horseOverlapModal: { show: false, conflicts: [], callback: null },
    sidebarOpen: false,
    firstLoad: true,
    autoActionFlags: { horseAssignRecurring: false }
  },
  mutations: {
    setWorkspaces (state, data) {
      const workspaces = data || [];

      // workspaces for test not pegase
      // workspaces.push({ code: 'alpha_rei', name: 'Grand Reims' });

      state.workspaces = sortArray(workspaces, 'name');
    },
    setShowOriginalSessionColor (state, data) {
      state.showOriginalSessionColor = data;
    },
    setShowDisciplineColor (state, data) {
      state.showDisciplineColor = data;
    },
    setShowSessionsOfType (state, data) {
      state.showSessionsOfType = data;
    },
    setIsSessionFilterApplied (state, data) {
      state.isSessionFilterApplied = data;
    },
    setAlgoliaCustomer (state, data) {
      state.algoliaCustomers.push(data);
    },
    setAlgoliaCustomers (state, data) {
      state.algoliaCustomers = data;
    },
    setSelectedCustomer (state, data) {
      state.selectedCustomers.push(data);
    },
    setSelectedCustomers (state, data) {
      state.selectedCustomers = data;
    },
    setSelectedSession (state, data) {
      state.selectedSession = data;
    },
    setAvailableHorses (state, data) {
      state.availableHorses = data;
    },
    setInstructorSessions (state, data) {
      state.instructorSessions = data;
    },
    setAmplifySubscription (state, data) {
      state.amplifySubscription = data;
    },
    setUser (state, data) {
      state.user = data;
    },
    setWorkspace (state, data) {
      state.workspace = data;
    },
    setSelectedDate (state, data) {
      state.selectedDate = data;
    },
    setLoading (state, data) {
      state.loading = data;
    },
    setSearch (state, data) {
      state.search = data;
    },
    setCurrentTab (state, data) {
      state.currentTab = data;
    },
    setReferences (state, data) {
      state.references = data;
    },
    setFilters (state, data) {
      state.filters = data;
    },
    setFilterType (state, data) {
      state.filterType = data;
    },
    setHorses (state, data) {
      state.horses = data;
    },
    setPlaces (state, data) {
      state.places = data;
    },
    setInstructors (state, data) {
      state.instructors = data;
    },
    setSessions (state, data) {
      state.sessions = data;
    },
    setDisciplines (state, data) {
      state.disciplines = data;
    },
    setParticipants (state, data) {
      state.participants = data;
    },
    setSocket (state, data) {
      state.socket = data;
    },
    setSideBarFilters (state, data) {
      state.sideBarFilters = data;
    },
    setFilterResults (state, data) {
      state.filterResults = data;
    },
    setCustomers (state, data) {
      state.customers = data;
    },
    setContentTypeSessions (state, data) {
      state.contentTypeAllSessions = data;
    },
    setComponentLoader (state, data) {
      state.componentLoader = data;
    },
    setSiteInfo (state, data) {
      state.siteInfo = data;
    },
    setProducts (state, data) {
      state.products = data;
    },
    setCustomersToAssign (state, data) {
      state.customersToAssign = data;
    },
    setCurrentStep (state, data) {
      state.currentStep = data;
    },
    setMonthlySessions (state, data) {
      state.monthlySessions = data;
    },
    setWebSocketError (state, data) {
      state.webSocketError = data;
    },
    setDebugMode (state, data) {
      state.debugMode = data;
    },
    setNetworkLogs (state, data) {
      state.networkLogs.push(data);
    },
    setMaxEffortCallback (state, callback) {
      state.maxEffortCallback = callback;
    },
    setShowMaxEffortModal (state, data) {
      state.showMaxEffortModal = data;
    },
    setAlertEffort (state, data) {
      state.alertEffort = data;
    },
    setIsLazyLoaded (state, data) {
      const { type, value } = data;
      if (type && typeof value === 'boolean') {
        state.isLazyLoaded[type] = value;
      }
    },
    resetLazyLoadResources (state) {
      state.isLazyLoaded = {
        horses: false,
        places: false,
        instructors: false,
        products: false
      };
      state.products = [];
      state.horses = [];
      state.instructors = [];
      state.places = [];
    },
    setWorkspaceActivitiesFilter (state, data) {
      state.workspaceActivitiesFilter = data;
    },
    setSynchronizePlanning (state, data) {
      state.synchronizePlanning = data;
    },
    setSwapHorseModal (state, { show = false, callback = null }) {
      state.swapHorseModal = { show, callback: (typeof callback) === 'function' ? callback : null };
    },
    setHorseOverlapModal (state, { show = false, conflicts = [], callback = null }) {
      state.horseOverlapModal = { show, conflicts, callback: (typeof callback) === 'function' ? callback : null };
    },
    setSidebarOpen (state, value) {
      state.sidebarOpen = value;
    },
    setFirstLoad (state, data) {
      state.firstLoad = data;
    },
    setAutoActionFlags (state, data) {
      const { type, value } = data;
      if (type && typeof value === 'boolean' && Object.keys(state.autoActionFlags).includes(type)) {
        state.autoActionFlags[type] = value;
      }
    }
  },
  actions: {
    async instructorSessions (context) {
      const start = startOfDay(new Date());
      const end = add(start, { years: 1 });

      let sessions = await contentTypeSessions({ ...context.state.user, wtype: 'collaborator' }, +format(start, 't'), +format(end, 't'));
      sessions = sessions.filter(s => isAfter(fromUnixTime(s.end_time), new Date()));
      context.commit('setInstructorSessions', sessions);
    },
    async getSiteInfo (context) {
      const response = await getSiteInfo();
      if (response.success) {
        context.commit('setSiteInfo', response.data);
        context.commit('setDebugMode', response.data?.debugMode);
      }
    },
    async getProducts (context) {
      if (context.state.isLazyLoaded.products) {
        return;
      }
      const products = await openSearchProducts();

      if (products.success) {
        const productsArray = products.data.filter(p => p).map(p => ({
          ...p,
          wtitle: p.name,
          activities: p.activities_types ? p.activities_types : []
        }));
        context.commit('setProducts', (productsArray || []));
        context.commit('setIsLazyLoaded', { type: 'products', value: true });
      }
    },
    async getHorses (context, workspace = context.state.workspace.code) {
      if (context.state.isLazyLoaded.horses) {
        return;
      }

      const response = await getHorses(workspace);

      if (response.success) {
        context.commit('setHorses', sortArray((response.data || []), 'horse_name'));
        context.commit('setIsLazyLoaded', { type: 'horses', value: true });
      }
    },
    async getPlaces (context, workspace = context.state.workspace.code) {
      if (context.state.isLazyLoaded.places) {
        return;
      }

      const response = await getPlaces(workspace);
      if (response.success) {
        context.commit('setPlaces', sortArray((response.data || []), 'place_name'));
        context.commit('setIsLazyLoaded', { type: 'places', value: true });
      }
    },
    async getDisciplines (context, workspace = context.state.workspace.code) {
      const response = await getDisciplines(workspace);

      if (response.success) {
        context.commit('setDisciplines', sortArray((response.data || []).map(d => ({ uuid: d.uuid, name: d.name, weightingFactor: d.weightingFactor, color: d.color })), 'name'));
      }
    },
    async getInstructors (context, workspace = context.state.workspace.code) {
      if (context.state.isLazyLoaded.instructors) {
        return;
      }

      const response = await getInstructors(workspace);

      if (response.success && response.data) {
        const instructors = response.data.map(instructor => {
          instructor.fullname = `${instructor.firstname} ${instructor.lastname}`;
          return instructor;
        });
        context.commit('setInstructors', sortArray(instructors, 'firstname'));
        context.commit('setIsLazyLoaded', { type: 'instructors', value: true });
      }
    },
    async getReferences (context) {
      const response = await equestrianReference();
      if (response.data) {
        context.commit('setReferences', response.data.references);
      }
    },
    async allMonthlySessions (context, contentType) {
      const { monthlySessions } = context.state;
      const { selectedDate, amplifyWorkspace } = context.getters;

      if (monthlySessions.length && monthlySessions.some(s => isSameMonth(fromUnixTime(s.start_time)))) {
        return;
      }
      const start = getUnixTime(startOfMonth(new Date(selectedDate)));
      const end = getUnixTime(endOfMonth(new Date(selectedDate)));

      const sessions = await getSessionsByPeriodWithContentType(start, end, amplifyWorkspace, contentType);
      context.commit('setMonthlySessions', sessions);
    },
    updateHorses (context, horses) {
      (horses || []).forEach(h => {
        const horse = context.getters.horse(h.uuid);
        if (horse) {
          const index = context.state.horses.findIndex(hors => hors.uuid === h.uuid);
          if (index !== -1) {
            context.state.horses[index] = { ...horse, owners: h.owners, isUcpaOwned: h.isUcpaOwned };
          }
        }
      });
    },
    setResetFilters ({ commit }) {
      commit('horsesFilters/resetFilters');
      commit('sessionsFilters/resetFilters');
    }
  },
  getters: {
    allowedWorkspaces: state => {
      const { workspaces, user } = state;

      if (user && (user.readWorkspaces || []).includes('*')) {
        return workspaces;
      }

      return workspaces.filter(workspace => (user.readWorkspaces || []).includes(workspace.code));
    },
    canUserPerformAction: state => {
      const { workspaces, user } = state;

      if (user && (user.readWorkspaces || []).includes('*')) {
        return true;
      }

      const allowedWorkspaces = workspaces.filter(workspace => (user.writeWorkspaces || []).includes(workspace.code));

      return !!allowedWorkspaces.length;
    },
    currentUserField: state => ({ firstname: state.user.firstname, lastname: state.user.lastname, name: state.user.name, uuid: state.user.uuid, email: state.user.email }),
    instructorSessions: state => {
      const now = new Date();
      const sessions = state.instructorSessions;

      const start = now;
      const end = add(now, { years: 1 });

      const monthsInInterval = eachMonthOfInterval({
        start: start,
        end: end
      });

      const returnValue = [];
      const daysWithSession = [];

      monthsInInterval.map((month, monthIndex) => {
        const monthValue = {
          label: format(month, 'MMMM yyyy', { locale: fr }),
          month: format(month, 'MMM', { locale: fr }),
          key: `month_${format(month, 'yyyy-MM-dd')}`,
          weeks: []
        };

        const startMonth = startOfMonth(month);
        const endMonth = endOfMonth(month);

        const weeksInMonth = eachWeekOfInterval({
          start: startMonth,
          end: endMonth
        }, { weekStartsOn: 1 });

        weeksInMonth.map(week => {
          const year = format(week, 'yyyy');
          const yearNow = format(now, 'yyyy');

          // if (year >= yearNow) {
          const startWeek = startOfWeek(week);
          const endWeek = endOfWeek(week);
          const isSameMonth = format(startWeek, 'MMMM') === format(endWeek, 'MMMM');
          const weekLabel = isSameMonth ? `${format(startWeek, 'd', { locale: fr })} - ${format(endWeek, 'd', { locale: fr })} ${monthValue.month}` : `${format(startWeek, 'd MMM', { locale: fr })} - ${format(endWeek, 'd MMM', { locale: fr })}`;

          const weekValue = {
            weekLabel,
            days: []
          };

          const datesToCreate = getDatesBetweenDates(format(startWeek, 'yyyy-MM-dd'), format(endWeek, 'yyyy-MM-dd'));

          datesToCreate.map(day => {
            const sessionsInDayInstructor = filterSessionByInterval(sessions, startOfDay(new Date(day)), endOfDay(new Date(day)));

            const sessionsInDay = sortArray(sessionsInDayInstructor, 'start_time');

            const dayValue = {
              day: format(new Date(day), 'd'),
              week_day: format(new Date(day), 'EEEE', { locale: fr }),
              day_label: format(new Date(day), 'yyyy-MM-dd'),
              sessions: sessionsInDay.map(session => ({
                ...session,
                timeLabel: `${format(session.start_time * 1000, 'HH:mm')} - ${format(session.end_time * 1000, 'HH:mm')}`
              })),
              dayHaveSession: !!sessionsInDay.length
            };

            if (dayValue.dayHaveSession) {
              daysWithSession.push(format(new Date(day), 'yyyy-MM-dd'));
            }

            weekValue.days.push(dayValue);
          });

          if (monthIndex !== 0) {
            const lastMonth = returnValue[returnValue.length - 1];

            const weekExist = lastMonth.weeks.some(w => w.weekLabel === weekValue.weekLabel);

            if (!weekExist) {
              monthValue.weeks.push({
                ...weekValue,
                doesDaysHaveSessions: weekValue.days.some(d => d.sessions.length)
              });
            }
            return;
          }

          monthValue.weeks.push({
            ...weekValue,
            doesDaysHaveSessions: weekValue.days.some(d => d.sessions.length)
          });
          // }
        });
        returnValue.push(monthValue);
      });

      return { sessions: returnValue, days: daysWithSession };
    },
    isIntructorOnMobile: state => {
      const { user, workspace } = state;
      return user?.resources?.some(resource => resource.type === 'monitor' && resource.workspace === workspace.code) && isMobileView();
    },
    displayOnPlannerCRM: state => state.displayOnPlannerCustomers.filter(customer => customer.reason === displayOnPlanner.ADDED_ON_CRM),
    displayOnPlannerContract: state => state.displayOnPlannerCustomers.filter(customer => customer.reason === displayOnPlanner.ACTIVE_CONTRACT),
    displayOnPlannerSearch: state => state.displayOnPlannerCustomers.filter(customer => customer.reason === displayOnPlanner.ACTIVE_CONTRACT && customer.show),
    amplifyWorkspace: state => state.workspace.code.toUpperCase(),
    selectedDate: state => state.selectedDate || format(new Date(), 'yyyy-MM-dd'),
    sideBarFilters: state => state.sideBarFilters || { session: { product_activity: {}, discipline: {} }, Absence: false, horse: { session: { type: [] } }, instructor: { session: { type: [] } }, customer: {} },
    horse: state => uuid => state.horses.find(h => h.uuid === uuid),
    instructor: state => uuid => state.instructors.find(i => i.uuid === uuid),
    place: state => uuid => state.places.find(i => i.uuid === uuid),
    session: state => uuid => state.sessions.find(s => s.uuid === uuid),
    customer: state => uuid => {
      const { with_session, without_session } = separateContentTypesWithAndWithoutSession('customer');

      const selectedCustomer = state.selectedCustomers.find(i => i.uuid === uuid);

      if (selectedCustomer) {
        return selectedCustomer;
      }

      const customers = [...with_session, ...without_session];

      return customers.find(i => i.uuid === uuid);
    },
    // sessionInstructors: state => session => sessionContentType(state.instructors, 'instructors', session),
    sessionPlace: state => session => sessionContentType(state.places, 'place', session),
    horsesByGender: state => gender => {
      const horsesArray = [];
      state.horses.filter(horse => {
        if (state.references) {
          const genderKey = Object.keys(state.references.equestrianGender || {}).find(key => (key === horse.horse_gender ? key : null));
          if (horse.horse_gender && genderKey === gender) {
            horsesArray.push({ value: horse.uuid, label: horse.horse_name });
          }
        }
      });
      return horsesArray;
    },
    referenceKey: state => (referenceValue, value) => {
      if (!state.references || isObjectEmpty(state.references)) {
        return null;
      }
      const equestrianRef = Object.entries(state.references[referenceValue]).find((([key, v]) => v === value));
      return equestrianRef ? equestrianRef[0] : null;
    },
    referenceValue: state => (referenceValue, value) => {
      if (!state.references || isObjectEmpty(state.references)) {
        return null;
      }

      const equestrianRef = Object.entries(state.references[referenceValue]).find((([key, v]) => key === value));
      return equestrianRef ? equestrianRef[1] : null;
    },
    // availableInstructors: state => session => availableContentType(state.instructors, 'instructors', session, state.sessions),
    // an unavailable place is a place there was on a session fermature
    availablePlaces: state => session => state.places.filter(place => (isContentTypeAvailable(place, session) ? place : null)),
    monthlySessions: (_, getters) => contentType => {
      const start = startOfMonth(new Date(getters.selectedDate));
      const end = endOfMonth(new Date(getters.selectedDate));
      return getContentTypeSessionsByPeriod(start, end, contentType);
    },
    dailySessions: (state, getters) => {
      const { sessions } = state;
      const { selectedDate } = getters;
      const start = startOfDay(new Date(selectedDate));
      const end = endOfDay(new Date(selectedDate));
      return filterSessionByInterval(sessions, start, end);
    },
    sortedDailySessions: (state, getters) => [...getters.dailySessions].sort((a, b) => {
      if (a.start_time !== b.start_time) {
        return a.start_time - b.start_time;
      }
      if (a.end_time !== b.end_time) {
        return a.end_time - b.end_time;
      }
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    }),
    sessionByDate: state => date => {
      const { sessions } = state;
      const start = startOfDay(new Date(date));
      const end = endOfDay(new Date(date));
      return filterSessionByInterval(sessions, start, end);
    },
    dailySessionsByContentType: (state, getters) => contentType => {
      const { sessions } = state;
      const { selectedDate } = getters;
      const start = startOfDay(new Date(selectedDate));
      const end = endOfDay(new Date(selectedDate));
      return getContentTypeSessionsByPeriod(start, end, contentType, sessions);
    },
    weeklySessionsByContentType: (state, getters) => contentType => {
      const { sessions } = state;
      const { selectedDate } = getters;
      const start = startOfWeek(new Date(selectedDate), { weekStartsOn: 1 });
      const end = endOfWeek(new Date(selectedDate), { weekStartsOn: 1 });

      return getContentTypeSessionsByPeriod(start, end, contentType, sessions);
    },
    monthlySessionsByContentType: (state, getters) => contentType => {
      const { selectedDate } = getters;
      const { monthlySessions } = state;

      const start = startOfMonth(new Date(selectedDate));
      const end = endOfMonth(new Date(selectedDate));

      return getContentTypeSessionsByPeriod(start, end, contentType, monthlySessions);
    },
    referenceByName: state => referenceName => {
      if (state.references && !isObjectEmpty(state.references)) {
        const types = Object.entries(state.references[referenceName] || {}).map(([key, value]) => ({
          value: key,
          label: value
        }));
        return sortArray(types, 'label');
      }
      return [];
    },
    horsesFromCustomer: state => customerUuid => {
      const { horses } = state;
      return customerUuid ? (horses || []).filter(h => (h.owners || []).some(o => o.ownerUuid === customerUuid)) : [];
    },
    horsesWithEffort: (state, getters) => (state.horses || []).map(h => ({ ...h, ...computeDailyWeeklyHorseEffort(h), monthlyEffort: minutesToHours(computeEffort(getters.monthlySessionsByContentType(h), h)) })),
    filteredRepriseTabSessions: (state, getters) => getters['sessionsFilters/repriseTab'](getters.sortedDailySessions),
    activeRepriseFiltersCount: state => {
      const horsesActive = (state.horsesFilters && state.horsesFilters.active) ? state.horsesFilters.active : {};
      const sessionsActive = (state.sessionsFilters && state.sessionsFilters.active) ? state.sessionsFilters.active : {};
      let count = 0;
      Object.keys(horsesFiltersDefaults).forEach(key => {
        if (!!horsesActive[key] && horsesActive[key] !== horsesFiltersDefaults[key]) {
          count += 1;
        }
      });
      Object.keys(sessionsFiltersDefaults).forEach(key => {
        if (!!sessionsActive[key] && sessionsActive[key] !== sessionsFiltersDefaults[key]) {
          count += 1;
        }
      });
      return count;
    },
    dailySoins: (state, getters) => {
      const { sessions } = state;
      const { selectedDate } = getters;
      const start = startOfDay(new Date(selectedDate));
      const end = endOfDay(new Date(selectedDate));
      const dailySessions = filterSessionByInterval(sessions, start, end);
      return (dailySessions || []).filter(s => s.type === 'SOINS');
    }
  },
  modules: {
    horsesFilters,
    sessionsFilters
  }
});
